import api from "utils/api";
import { API_ENDPOINTS } from "config/api";

export const MAX_UPLOAD_BYTES = 1024 * 1024 * 1024;
export const UPLOAD_POLL_INTERVAL_MS = 2500;
export const UPLOAD_IN_FLIGHT_STATUSES = ["uploading", "validating"];

const EXTENSIONS_BY_CONTENT_TYPE = {
  "audio/mp4": "m4a",
  "audio/mpeg": "mp3",
  "audio/ogg": "ogg",
  "audio/wav": "wav",
  "audio/webm": "webm",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
};

export class UploadCancelledError extends Error {
  constructor(message = "Upload cancelled.") {
    super(message);
    this.name = "UploadCancelledError";
  }
}

export function isUploadCancelledError(error) {
  return error instanceof UploadCancelledError || error?.name === "AbortError";
}

// Uploads now happen in two backend stages (S3 session, then transcript
// creation once validated) but should read as one step to the user. The only
// record of "there's an upload with this id in flight" lives client-side, so
// persist it the moment a session is created and let a fresh page load
// reattach by polling the same id — see the backend team's note on
// UploadSession lifecycle for why there's no server-side list to recover from.
const PERSISTED_UPLOADS_KEY = "arkii_uploads_inflight";

function readPersistedUploads() {
  try {
    const raw = localStorage.getItem(PERSISTED_UPLOADS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writePersistedUploads(records) {
  try {
    localStorage.setItem(PERSISTED_UPLOADS_KEY, JSON.stringify(records));
  } catch {
    // Storage may be unavailable (private browsing, quota). Worst case a
    // refresh can't reattach to this upload — the poll itself still works.
  }
}

export function findPersistedUpload(scope) {
  return readPersistedUploads().find((record) => record.scope === scope) ?? null;
}

export function savePersistedUpload(record) {
  const records = readPersistedUploads().filter((r) => r.scope !== record.scope);
  records.push(record);
  writePersistedUploads(records);
}

export function removePersistedUpload(id) {
  writePersistedUploads(readPersistedUploads().filter((r) => r.id !== id));
}

export function filenameForUpload(file, customName) {
  if (!customName && file?.name) return file.name;

  const baseName = customName || "recording";
  if (/\.[a-z0-9]{1,8}$/i.test(baseName)) return baseName;

  const extension = EXTENSIONS_BY_CONTENT_TYPE[file?.type] || "webm";
  return `${baseName}.${extension}`;
}

// Upload endpoints may return the resource directly or wrap it as
// `{ upload: {...}, request: {...} }`, matching other create endpoints in
// this API. Keep the transport code independent of that response envelope.
export function normalizeUploadResponse(payload) {
  const root = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const nested = root?.upload ?? root?.upload_session ?? root?.session;
  const upload = nested && typeof nested === "object" ? nested : root;

  if (!upload || typeof upload !== "object") return upload;
  return {
    ...root,
    ...upload,
    request: root?.request ?? upload.request,
  };
}

export function transcriptionFromUpload(upload, options = {}) {
  const linked = upload?.transcript;
  const id = linked?.id ?? upload?.transcript_id;
  if (!id) return null;

  return {
    ...linked,
    id,
    status: linked?.status || "pending",
    name: linked?.name || options.name || options.filename,
    source: linked?.source || options.source || "uploaded",
    project: linked?.project ?? options.project ?? null,
    language_requested: linked?.language_requested || options.language || "ar",
    created_at: linked?.created_at || new Date().toISOString(),
  };
}

function abortError() {
  return new UploadCancelledError();
}

function wait(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export function putFileToPresignedUrl(file, request, { signal, onProgress } = {}) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    if (!request?.url) {
      reject(new Error("The upload session did not include a storage URL."));
      return;
    }

    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();

    function cleanup() {
      signal?.removeEventListener("abort", abort);
    }

    xhr.open(request.method || "PUT", request.url, true);
    Object.entries(request.headers || {}).forEach(([name, value]) => {
      xhr.setRequestHeader(name, value);
    });

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        onProgress?.(Math.round((event.loaded / event.total) * 100));
      }
    });
    xhr.addEventListener("load", () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`Storage rejected the upload (HTTP ${xhr.status}).`));
      }
    });
    xhr.addEventListener("error", () => {
      cleanup();
      reject(new Error("The file could not be uploaded to storage."));
    });
    xhr.addEventListener("abort", () => {
      cleanup();
      reject(abortError());
    });

    signal?.addEventListener("abort", abort, { once: true });
    xhr.send(file);
  });
}

export async function pollUploadStatus(uploadId, { signal, onStatus } = {}) {
  while (true) {
    if (signal?.aborted) throw abortError();

    const { data } = await api.get(API_ENDPOINTS.UPLOAD.DETAIL(uploadId), { signal });
    const upload = normalizeUploadResponse(data);
    onStatus?.(upload);
    if (!UPLOAD_IN_FLIGHT_STATUSES.includes(upload?.status)) return upload;

    await wait(UPLOAD_POLL_INTERVAL_MS, signal);
  }
}

export async function cancelUploadSession(uploadId) {
  try {
    await api.delete(API_ENDPOINTS.UPLOAD.DETAIL(uploadId));
  } catch (error) {
    throw new Error("The upload stopped, but the server could not finish cancelling it.", { cause: error });
  }
}

export function statusError(upload) {
  if (upload?.status === "failed") {
    return new Error(upload.failure_reason || "The server could not verify the uploaded file.");
  }
  if (upload?.status === "expired") return new Error("The upload session expired. Please try again.");
  if (upload?.status === "cancelled") return new UploadCancelledError();
  return new Error(`Unexpected upload status: ${upload?.status || "unknown"}.`);
}

export async function uploadFileDirectly(
  file,
  {
    filename = filenameForUpload(file),
    language = "ar",
    source = "uploaded",
    name = filename,
    project = null,
    signal,
    onProgress,
    onSession,
    onStatus,
    onTransferComplete,
  } = {}
) {
  if (!file) throw new Error("Choose a file to upload.");
  if (file.size > MAX_UPLOAD_BYTES) throw new Error("File is larger than the 1 GiB upload limit.");

  // MediaRecorder produces a Blob. Wrap it without encoding so every S3 PUT
  // still receives the raw File shape required by the upload contract.
  const uploadFile =
    file instanceof File
      ? file
      : new File([file], filename, { type: file.type || "application/octet-stream", lastModified: Date.now() });

  const { data: sessionPayload } = await api.post(
    API_ENDPOINTS.UPLOAD.LIST,
    {
      filename,
      size_bytes: uploadFile.size,
      content_type: uploadFile.type || "application/octet-stream",
      language,
      source,
      name,
      project,
    },
    { signal }
  );

  const session = normalizeUploadResponse(sessionPayload);
  const uploadId = session?.id ?? session?.upload_id ?? session?.uuid;
  if (!uploadId) throw new Error("The upload session did not include an upload ID.");
  onSession?.(session);

  if (signal?.aborted) {
    await cancelUploadSession(uploadId);
    throw abortError();
  }

  const pollController = new AbortController();
  const stopPolling = () => pollController.abort();
  signal?.addEventListener("abort", stopPolling, { once: true });

  const transferPromise = putFileToPresignedUrl(uploadFile, session.request, { signal, onProgress });

  // Poll as soon as the S3 transfer starts. Wrapping the result prevents an
  // early polling failure from becoming an unhandled rejection while the PUT
  // is still in flight.
  const statusPromise = pollUploadStatus(uploadId, {
    signal: pollController.signal,
    onStatus,
  }).then(
    (value) => ({ value }),
    (error) => ({ error })
  );

  try {
    await transferPromise;
    onTransferComplete?.();

    const outcome = await statusPromise;
    if (outcome.error) throw outcome.error;
    if (outcome.value?.status !== "uploaded") throw statusError(outcome.value);
    return outcome.value;
  } catch (error) {
    stopPolling();
    await statusPromise;
    if (signal?.aborted) {
      await cancelUploadSession(uploadId);
      throw abortError();
    }
    throw error;
  } finally {
    signal?.removeEventListener("abort", stopPolling);
  }
}
